import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_TOLERANCE = 1;

/**
 * 滚动箭头 Hook
 * 用于管理滚动箭头的禁用状态和滚动行为
 * @returns {
 *  scrollContainerRef: 滚动容器 ref
 *  isLeftDisabled: 左箭头是否禁用
 *  isRightDisabled: 右箭头是否禁用
 *  checkScrollPosition: 检查滚动位置并更新箭头禁用状态
 *  handleScrollLeft: 处理左箭头点击，向左滚动一屏
 *  handleScrollRight: 处理右箭头点击，向右滚动一屏
 * }
 */
export const useScrollArrow = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);

  /**
   * 检查滚动位置并更新箭头禁用状态
   */
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    // 滚动到最左边时禁用左箭头
    const isAtLeft = scrollLeft === 0;
    // 滚动到最右边时禁用右箭头
    const isAtRight = scrollLeft + clientWidth >= scrollWidth - SCROLL_TOLERANCE;

    setIsLeftDisabled((prev) => (prev !== isAtLeft ? isAtLeft : prev));
    setIsRightDisabled((prev) => (prev !== isAtRight ? isAtRight : prev));
  }, []);

  /**
   * 监听滚动事件，更新箭头禁用状态
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // 清理函数
    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  /**
   * 处理左箭头点击，向左滚动一屏
   */
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  /**
   * 处理右箭头点击，向右滚动一屏
   */
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return {
    scrollContainerRef,
    isLeftDisabled,
    isRightDisabled,
    checkScrollPosition,
    handleScrollLeft,
    handleScrollRight,
  };
};
